import {
  CreatePartnerSkuModifierGroupRequest,
  CreatePartnerSkuModifierGroupResponse,
  UpdatePartnerSkuModifierGroupRequest,
  UpdatePartnerSkuModifierGroupResponse,
  GetPartnerSkuModifierGroupsResponse,
  CreatePartnerSkuModifierRequest,
  CreatePartnerSkuModifierResponse,
  UpdatePartnerSkuModifierRequest,
  UpdatePartnerSkuModifierResponse,
  LinkSkuModifierGroupToItemsRequest,
} from '@classpass/protobuf-definitions/lib/web/grpc/inventory/manual_partners';

import URLS from 'constants/urls';
import { transformKeysToCamelCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';

const parseProtoResponse = (response, parser) => transformKeysToCamelCase(parser.fromJSON(response.data));

// Some create/update endpoints return the entity at the top level of response.data instead
// of inside its { <wrapperKey>: … } envelope; re-nest so the proto parser sees the shape it
// expects (same defensive pattern as SkuItems.helpers.js).
const parseWrappedEntity = (response, ResponseType, wrapperKey, snakeWrapperKey) => {
  const parsed = parseProtoResponse(response, ResponseType);
  if (!parsed[wrapperKey]?.core?.id && response.data?.core?.id) {
    return parseProtoResponse({ data: { [snakeWrapperKey]: response.data } }, ResponseType)[wrapperKey];
  }
  return parsed[wrapperKey];
};

const parseGroupResponse = (response, ResponseType) =>
  parseWrappedEntity(response, ResponseType, 'modifierGroup', 'modifier_group');

const parseModifierResponse = (response, ResponseType) =>
  parseWrappedEntity(response, ResponseType, 'modifier', 'modifier');

const buildModifierInputs = modifiers =>
  (modifiers || []).map(modifier => ({
    name: modifier.name,
    description: modifier.description,
    ordinal: modifier.ordinal,
    is_default: modifier.isDefault,
    enabled: modifier.enabled,
    sold_out: modifier.soldOut,
  }));

export const httpModifierAdapter = {
  async createModifierGroup(venueId, { name, description, selectionsMin, selectionsMax, allowsQuantities, modifiers }) {
    try {
      const url = URLS.sku.modifierGroups(venueId);
      const requestBody = CreatePartnerSkuModifierGroupRequest.toJSON({
        venue_id: venueId,
        name,
        description,
        selections_min: selectionsMin,
        selections_max: selectionsMax,
        allows_quantities: allowsQuantities,
        modifiers: buildModifierInputs(modifiers),
      });
      const response = await httpClient.post(url, requestBody);
      return parseGroupResponse(response, CreatePartnerSkuModifierGroupResponse);
    } catch (error) {
      reportError('Error creating SKU modifier group', error);
      throw error;
    }
  },

  async updateModifierGroup(
    venueId,
    groupId,
    { name, description, selectionsMin, selectionsMax, allowsQuantities, enabled, partnerState, modifierOrder }
  ) {
    try {
      const url = URLS.sku.modifierGroup(venueId, groupId);
      const requestBody = {
        ...UpdatePartnerSkuModifierGroupRequest.toJSON({
          modifier_group_id: groupId,
          name,
          description,
          selections_min: selectionsMin,
          selections_max: selectionsMax,
          allows_quantities: allowsQuantities,
          enabled,
          modifier_order: modifierOrder,
        }),
        ...(partnerState !== undefined ? { partner_state: partnerState } : {}),
      };
      const response = await httpClient.put(url, requestBody);
      return parseGroupResponse(response, UpdatePartnerSkuModifierGroupResponse);
    } catch (error) {
      reportError('Error updating SKU modifier group', error);
      throw error;
    }
  },

  async getModifierGroups(venueId, { enabled, published } = {}) {
    try {
      const url = URLS.sku.modifierGroups(venueId);
      const response = await httpClient.get(url, { params: { enabled, published } });
      const parsed = parseProtoResponse(response, GetPartnerSkuModifierGroupsResponse);
      return parsed.modifierGroups || [];
    } catch (error) {
      reportError('Error fetching SKU modifier groups', error);
      throw error;
    }
  },

  async createModifier(venueId, groupId, { name, description, ordinal, isDefault, enabled, soldOut }) {
    try {
      const url = URLS.sku.modifiers(venueId, groupId);
      const requestBody = CreatePartnerSkuModifierRequest.toJSON({
        modifier_group_id: groupId,
        name,
        description,
        ordinal,
        is_default: isDefault,
        enabled,
        sold_out: soldOut,
      });
      const response = await httpClient.post(url, requestBody);
      return parseModifierResponse(response, CreatePartnerSkuModifierResponse);
    } catch (error) {
      reportError('Error creating SKU modifier', error);
      throw error;
    }
  },

  async updateModifier(
    venueId,
    modifierId,
    { name, description, ordinal, isDefault, soldOut, enabled, partnerState, flipBackAt }
  ) {
    try {
      const url = URLS.sku.modifier(venueId, modifierId);
      const requestBody = {
        ...UpdatePartnerSkuModifierRequest.toJSON({
          modifier_id: modifierId,
          name,
          description,
          ordinal,
          is_default: isDefault,
          sold_out: soldOut,
          enabled,
        }),
        ...(partnerState !== undefined ? { partner_state: partnerState } : {}),
        ...(flipBackAt !== undefined ? { flip_back_at: flipBackAt } : {}),
      };
      const response = await httpClient.put(url, requestBody);
      return parseModifierResponse(response, UpdatePartnerSkuModifierResponse);
    } catch (error) {
      reportError('Error updating SKU modifier', error);
      throw error;
    }
  },

  async linkModifierGroupToItems(venueId, groupId, itemIds) {
    try {
      const url = URLS.sku.linkModifierGroup(venueId, groupId);
      const requestBody = LinkSkuModifierGroupToItemsRequest.toJSON({
        modifier_group_id: groupId,
        item_ids: itemIds,
      });
      await httpClient.post(url, requestBody);
    } catch (error) {
      reportError('Error linking SKU modifier group to items', error);
      throw error;
    }
  },

  async unlinkModifierGroupFromItem(venueId, groupId, itemId) {
    try {
      const url = URLS.sku.unlinkModifierGroup(venueId, groupId, itemId);
      await httpClient.delete(url);
    } catch (error) {
      reportError('Error unlinking SKU modifier group from item', error);
      throw error;
    }
  },
};
